import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface FeaturePermissionMap {
  readonlyPerms: string[];
  viewPerms: string[];
  modifyPerms: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ReadOnlyService {
  /**
   * Fail-safe initial state is TRUE.
   * Remains true until permissions are loaded and verified.
   */
  private isReadOnlySubject = new BehaviorSubject<boolean>(true);
  public isReadOnly$: Observable<boolean> = this.isReadOnlySubject.asObservable();

  private permissionsLoaded = false;
  private currentPermissions: string[] = [];
  private rawPermissions: any[] = [];
  private currentUserData: any = null;

  // Registered mappings for standard modules
  private featureMap: { [key: string]: FeaturePermissionMap } = {
    'project': {
      readonlyPerms: ['readonly_project', 'readonly_project_profile', 'read_only_project', 'project_readonly', 'read_only'],
      viewPerms: ['view_project_profile', 'project_list_view'],
      modifyPerms: ['add_project_profile', 'edit_project_profile', 'delete_project_profile', 'assign_project_to_user']
    },
    'sub_contractor': {
      readonlyPerms: ['readonly_sub_contractor', 'readonly_subcontractor', 'readonly_lc_profile', 'read_only_sub_contractor', 'read_only'],
      viewPerms: ['view_lc_company_profile', 'lc_list_view'],
      modifyPerms: ['edit_lc_profile', 'delete_lc_profile', 'reactivate_lc_profile']
    },
    'subcontractor': {
      readonlyPerms: ['readonly_sub_contractor', 'readonly_subcontractor', 'readonly_lc_profile', 'read_only_sub_contractor', 'read_only'],
      viewPerms: ['view_lc_company_profile', 'lc_list_view'],
      modifyPerms: ['edit_lc_profile', 'delete_lc_profile', 'reactivate_lc_profile']
    },
    'work_order': {
      readonlyPerms: ['readonly_work_order', 'readonly_workorder', 'read_only_work_order', 'read_only'],
      viewPerms: ['view_work_order'],
      modifyPerms: ['add_work_order', 'edit_work_order']
    },
    'work-order': {
      readonlyPerms: ['readonly_work_order', 'readonly_workorder', 'read_only_work_order', 'read_only'],
      viewPerms: ['view_work_order'],
      modifyPerms: ['add_work_order', 'edit_work_order']
    },
    'requisition': {
      readonlyPerms: ['readonly_requisition', 'read_only_requisition', 'requisition_readonly', 'requisition_read_only', 'read_only'],
      viewPerms: ['requisition_list_view', 'search_requisition'],
      modifyPerms: ['add_requisition', 'edit_requisition', 'delete_requisition', 'shortlist_requisition', 'blacklist_requisition', 'reactivate_requisition']
    },
    'supply': {
      readonlyPerms: ['readonly_supply', 'read_only_supply', 'supply_readonly', 'supply_read_only', 'read_only'],
      viewPerms: ['supply_list_view', 'search_supply'],
      modifyPerms: ['add_supply', 'edit_supply', 'delete_supply', 'shortlist_supply', 'blacklist_supply', 'reactivate_supply']
    },
    'general_contractor': {
      readonlyPerms: ['readonly_general_contractor', 'readonly_gc_profile', 'read_only_general_contractor', 'read_only'],
      viewPerms: ['view_gc_company_profile', 'gc_list_view_profiles'],
      modifyPerms: ['edit_gc_profile', 'delete_gc_profile']
    },
    'general-contractor': {
      readonlyPerms: ['readonly_general_contractor', 'readonly_gc_profile'],
      viewPerms: ['view_gc_company_profile', 'gc_list_view_profiles'],
      modifyPerms: ['edit_gc_profile', 'delete_gc_profile']
    },
    'supplier': {
      readonlyPerms: ['readonly_supplier', 'readonly_supplier_profile'],
      viewPerms: ['view_supplier_profile', 'supplier_list_view'],
      modifyPerms: ['add_supplier', 'edit_supplier', 'delete_supplier', 'reactivate_supplier']
    },
    'dpr': {
      readonlyPerms: ['readonly_dpr'],
      viewPerms: ['view_dpr'],
      modifyPerms: ['add_dpr', 'edit_dpr', 'delete_dpr']
    },
    'bill': {
      readonlyPerms: ['readonly_bill', 'readonly_inward_billing'],
      viewPerms: ['view_bill'],
      modifyPerms: ['add_bill', 'edit_bill']
    },
    'inward-billing': {
      readonlyPerms: ['readonly_bill', 'readonly_inward_billing'],
      viewPerms: ['view_bill'],
      modifyPerms: ['add_bill', 'edit_bill']
    },
    'users': {
      readonlyPerms: ['readonly_user', 'readonly_users'],
      viewPerms: ['bo_user_list_view', 'gc_user_list_view', 'lc_user_list_view'],
      modifyPerms: ['add_bo_user', 'edit_bo_user', 'delete_bo_user', 'edit_gc_user', 'edit_lc_user']
    },
    'user': {
      readonlyPerms: ['readonly_user', 'readonly_users'],
      viewPerms: ['bo_user_list_view', 'gc_user_list_view', 'lc_user_list_view'],
      modifyPerms: ['add_bo_user', 'edit_bo_user', 'delete_bo_user', 'edit_gc_user', 'edit_lc_user']
    },
    'masters': {
      readonlyPerms: ['readonly_master', 'readonly_masters'],
      viewPerms: ['master_configuration_list_view', 'filter_search_masters'],
      modifyPerms: ['add_master', 'edit_master', 'delete_master']
    },
    'master': {
      readonlyPerms: ['readonly_master', 'readonly_masters'],
      viewPerms: ['master_configuration_list_view', 'filter_search_masters'],
      modifyPerms: ['add_master', 'edit_master', 'delete_master']
    },
    'bulk_notification': {
      readonlyPerms: ['readonly_bulk_notification'],
      viewPerms: ['view_bulk_notification'],
      modifyPerms: ['add_bulk_notification', 'edit_bulk_notification', 'delete_bulk_notification', 'publish_bulk_notification']
    },
    'bulk-notification': {
      readonlyPerms: ['readonly_bulk_notification'],
      viewPerms: ['view_bulk_notification'],
      modifyPerms: ['add_bulk_notification', 'edit_bulk_notification', 'delete_bulk_notification', 'publish_bulk_notification']
    }
  };

  constructor() {
    this.refreshState();
  }

  /**
   * Refreshes internal state from localStorage / sessionStorage / user_data.
   */
  public refreshState(): void {
    const rawUserData = localStorage.getItem('user_data');
    if (rawUserData) {
      try {
        this.currentUserData = JSON.parse(rawUserData);
      } catch (e) {
        this.currentUserData = null;
      }
    } else {
      this.currentUserData = null;
    }

    this.evaluateGlobalReadOnly();
  }

  /**
   * Synchronize fresh permissions from PermissionsService.
   */
  public setPermissions(permissions: any[]): void {
    this.permissionsLoaded = true;
    this.rawPermissions = Array.isArray(permissions) ? permissions : [];
    if (Array.isArray(permissions)) {
      const extracted: string[] = [];
      for (const p of permissions) {
        if (!p) continue;
        if (typeof p === 'string') {
          extracted.push(p);
        } else {
          if (p.permission_name) extracted.push(p.permission_name);
          if (p.name) extracted.push(p.name);
          const permNameLower = (p.permission_name || p.name || '').toLowerCase().trim();
          const dispNameLower = (p.display_name || '').toLowerCase().trim();
          const modNameLower = (p.module_name || p.module || '').toLowerCase().trim().replace(/-/g, '_');

          const isReadOnlyFlag = permNameLower === 'read_only' || permNameLower === 'readonly' ||
            permNameLower === 'read-only' || dispNameLower === 'read only' || dispNameLower === 'read-only' ||
            dispNameLower === 'readonly';

          if (isReadOnlyFlag && modNameLower) {
            extracted.push(`readonly_${modNameLower}`);
            extracted.push(`read_only_${modNameLower}`);
            extracted.push(`${modNameLower}_readonly`);
            extracted.push(`${modNameLower}_read_only`);
          }
        }
      }
      this.currentPermissions = Array.from(new Set(extracted));
    } else {
      this.currentPermissions = [];
    }
    this.evaluateGlobalReadOnly();
  }

  /**
   * Fail-safe notification when permission loading fails.
   */
  public markPermissionsFailed(): void {
    this.permissionsLoaded = false;
    this.currentPermissions = [];
    this.isReadOnlySubject.next(true); // Fail-safe: locked to read-only
  }

  /**
   * Programmatic override for testing or session switching.
   */
  public setReadOnly(isReadOnly: boolean): void {
    if (isReadOnly) {
      sessionStorage.setItem('read_only_mode', 'true');
    } else {
      sessionStorage.removeItem('read_only_mode');
      localStorage.removeItem('is_read_only');
    }
    this.isReadOnlySubject.next(isReadOnly);
  }

  /**
   * Returns current global read-only status synchronously.
   */
  public isReadOnly(): boolean {
    return this.isReadOnlySubject.getValue();
  }

  /**
   * Checks whether a specific component or feature is in read-only mode.
   *
   * @param feature Feature key or name (e.g. 'sub_contractor', 'project', 'work_order')
   */
  public isComponentReadOnly(feature?: string | string[]): boolean {
    // 1. If global read-only is active, all components are read-only
    if (this.isReadOnly()) {
      return true;
    }

    if (!feature) {
      return false;
    }

    const featureKeys = Array.isArray(feature) ? feature : [feature];

    for (const key of featureKeys) {
      const normalizedKey = key.trim().toLowerCase().replace(/-/g, '_');

      // Direct check against synthesized and raw permission keys
      const directCandidates = [
        `readonly_${normalizedKey}`,
        `read_only_${normalizedKey}`,
        `${normalizedKey}_readonly`,
        `${normalizedKey}_read_only`,
        `readonly_${key.toLowerCase()}`,
        `read_only_${key.toLowerCase()}`
      ];

      if (directCandidates.some(c => this.hasRawPermission(c))) {
        return true;
      }

      // Check raw permission objects array
      if (Array.isArray(this.rawPermissions)) {
        const hasModuleObjReadOnly = this.rawPermissions.some((p: any) => {
          if (!p || typeof p === 'string') return false;
          const pMod = (p.module_name || p.module || '').toLowerCase().trim().replace(/-/g, '_');
          if (pMod !== normalizedKey && pMod !== key.toLowerCase()) return false;

          const pName = (p.permission_name || p.name || '').toLowerCase().trim();
          const pDisp = (p.display_name || '').toLowerCase().trim();

          return pName === 'read_only' || pName === 'readonly' || pName === 'read-only' ||
                 pDisp === 'read only' || pDisp === 'readonly' || pDisp === 'read-only' ||
                 pName.includes('read_only') || pName.includes('readonly');
        });

        if (hasModuleObjReadOnly) {
          return true;
        }
      }

      // Check registered feature mapping
      const mapping = this.featureMap[key] || this.featureMap[normalizedKey];
      if (mapping) {
        // Explicit readonly flag ALWAYS takes precedence over modify permissions
        const hasExplicitReadOnly = mapping.readonlyPerms.some(rp => this.hasRawPermission(rp));
        if (hasExplicitReadOnly) {
          return true;
        }

        // Check if user has view permission but lacks ALL modify permissions
        const hasView = mapping.viewPerms.some(vp => this.hasRawPermission(vp));
        const hasAnyModify = mapping.modifyPerms.some(mp => this.hasRawPermission(mp));
        if (hasView && !hasAnyModify) {
          return true;
        }
      } else {
        // Dynamic fallback: check if any permission starts with 'readonly_' matching key
        const dynamicHasReadOnly = this.currentPermissions.some(p =>
          directCandidates.includes(p.toLowerCase()) || p.toLowerCase().startsWith(`readonly_${normalizedKey}`)
        );
        if (dynamicHasReadOnly) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Observable stream for a specific component's read-only status.
   */
  public isComponentReadOnly$(feature?: string | string[]): Observable<boolean> {
    return this.isReadOnly$.pipe(
      map(() => this.isComponentReadOnly(feature))
    );
  }

  /**
   * Checks if user has a raw permission in currentPermissions.
   */
  public hasRawPermission(permName: string): boolean {
    if (!permName || !this.currentPermissions || this.currentPermissions.length === 0) {
      return false;
    }
    const target = permName.toLowerCase().trim();
    return this.currentPermissions.some(p => p.toLowerCase().trim() === target);
  }

  /**
   * Evaluates global read-only state.
   */
  private evaluateGlobalReadOnly(): void {
    // 1. Fail-safe: if token exists but permissions not yet loaded
    const token = localStorage.getItem('token');
    if (token && !this.permissionsLoaded) {
      this.isReadOnlySubject.next(true);
      return;
    }

    // 2. Explicit session / local storage toggle
    const sessionOverride = sessionStorage.getItem('read_only_mode');
    const localOverride = localStorage.getItem('is_read_only');
    if (sessionOverride === 'true' || localOverride === 'true') {
      this.isReadOnlySubject.next(true);
      return;
    }

    // 3. User metadata check
    if (this.currentUserData) {
      if (this.currentUserData.is_read_only === true || this.currentUserData.read_only === true) {
        this.isReadOnlySubject.next(true);
        return;
      }
      const roleName = this.currentUserData.role?.name?.toLowerCase() || '';
      if (this.currentUserData.role?.is_read_only === true || roleName.includes('read only') || roleName.includes('viewer')) {
        this.isReadOnlySubject.next(true);
        return;
      }
    }

    // 4. Permissions check for global read-only permissions
    if (this.hasRawPermission('readonly_all') || this.hasRawPermission('readonly_mode')) {
      this.isReadOnlySubject.next(true);
      return;
    }

    // Otherwise, global read-only is false
    this.isReadOnlySubject.next(false);
  }
}
